import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import GlobalNav from '@/components/GlobalNav';
import { Button } from '@/components/ui/button';
import { ArrowRight, Eye, Layers, Wrench, ShieldCheck, KeyRound, Activity } from 'lucide-react';

const TECH_STAGES = [
  {
    icon: Eye,
    title: 'Deep Business Recognition',
    desc: 'We analyze 100% of your site\u2019s content to understand your actual business model, your primary goals, and the specific needs of your target audience.',
    color: 'from-blue-500 to-blue-700',
    shadow: 'shadow-blue-500/20',
  },
  {
    icon: Layers,
    title: 'Contextual AI Validation',
    desc: 'We verify how your site fits into your local niche and what signals it sends to the market. We test whether AI can actually find answers to the questions your customers are asking.',
    color: 'from-purple-500 to-purple-700',
    shadow: 'shadow-purple-500/20',
  },
  {
    icon: Wrench,
    title: 'Deterministic Fixer Engine',
    desc: 'We don\u2019t provide vague advice. Our system generates precise, \u201cReady-to-deploy\u201d code snippets and personalized prompts that allow you to fix critical issues in minutes without hiring a developer.',
    color: 'from-emerald-500 to-emerald-700',
    shadow: 'shadow-emerald-500/20',
  },
];

const SECURITY_PRINCIPLES = [
  {
    icon: ShieldCheck,
    title: 'Zero Integration, Zero Risk',
    desc: 'We never require access to your sensitive data, passwords, or backend. We scan your site from the outside\u2014exactly as it is seen by AI models and your customers.',
  },
  {
    icon: KeyRound,
    title: 'Ownership of Fixes',
    desc: 'Many tools offer \u201clive\u201d optimization where changes only work as long as you pay the monthly fee. If you cancel, the code is removed, and your site breaks. With ScoreCite, the action plan and the code we provide belong to you forever. It is your permanent capital.',
  },
];

export default function AboutPage() {
  return (
    <>
      <Helmet>
        <title>About ScoreCite \u2013 Your Brand in the Era of AI Answers</title>
        <meta name="description" content="Learn why ScoreCite exists. We bridge the gap between traditional SEO and the new world of AI-powered answers, ensuring your brand is quoted as the expert." />
        <meta name="robots" content="index, follow" />
        <script type="application/ld+json">
          {`{
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "ScoreCite",
            "url": "https://www.scorecite.com"
          }`}
        </script>
      </Helmet>

      <GlobalNav />

      <div className="min-h-screen bg-[#0A0A0A]">

        {/* Hero */}
        <section className="pt-20 pb-16 px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-6 animate-slide-up" data-testid="about-heading">
              About ScoreCite: Your Brand in the Era of AI&nbsp;Answers
            </h1>
            <p className="text-zinc-400 text-sm sm:text-base leading-relaxed max-w-2xl mx-auto animate-fade-in-scroll">
              Most analytical tools are stuck in the past, measuring only search engine rankings. At ScoreCite, we look at where the market is going: the intersection of AI&nbsp;Readiness and Generative Engine Optimization&nbsp;(GEO).
            </p>
          </div>
        </section>

        {/* Our Vision */}
        <section className="pb-16 px-4">
          <div className="max-w-3xl mx-auto">
            <div className="p-6 sm:p-8 rounded-xl border border-white/10 bg-white/[0.02] animate-fade-in-scroll hover:border-white/20 transition-all duration-300">
              <h2 className="font-heading text-lg sm:text-xl font-bold text-white mb-5" data-testid="vision-section">Our Vision: From Finding Links to Receiving Answers</h2>
              <div className="space-y-4 text-zinc-300 text-sm sm:text-base leading-relaxed">
                <p>
                  Traditional SEO taught us how to fight for a spot on a list of blue links. Today, the world is shifting. Your customers are no longer just browsing search results&mdash;they are asking questions to AI assistants like ChatGPT, Perplexity, and Gemini.
                </p>
                <p className="text-zinc-400">
                  The problem? If your website doesn&rsquo;t send clear, structured signals, AI will simply ignore you.
                </p>
                <p>
                  ScoreCite was built to bridge this gap. We don&rsquo;t replace SEO; we evolve it. We ensure your brand becomes a trusted source that AI models quote as the expert.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* The Hallucination Barrier */}
        <section className="pb-16 px-4 border-t border-white/5 pt-16">
          <div className="max-w-3xl mx-auto">
            <div className="p-6 sm:p-8 rounded-xl border border-white/10 bg-white/[0.02] animate-fade-in-scroll hover:border-white/20 transition-all duration-300" style={{ animationDelay: '0.1s' }}>
              <h2 className="font-heading text-lg sm:text-xl font-bold text-white mb-5" data-testid="hallucination-section">
                The &ldquo;Hallucination Barrier&rdquo;: Why AI Stays Silent About You
              </h2>
              <div className="space-y-4 text-zinc-300 text-sm sm:text-base leading-relaxed">
                <p>
                  All advanced Large Language Models (LLMs) are trained with one overriding priority: <span className="text-white font-medium">eliminating hallucinations</span> (making up facts).
                </p>
                <p className="text-zinc-400">
                  When an AI model is uncertain about a business&mdash;due to a lack of structured data (Schema), vague service descriptions, or missing expert-led content&mdash;its defense mechanism kicks in.
                </p>
                <p>
                  To avoid the risk of providing a false answer, the AI will simply <span className="text-white font-medium">omit your brand</span> from its response.
                </p>
                <p className="text-zinc-400">
                  Your company becomes invisible not because your services are poor, but because the AI &ldquo;fears&rdquo; talking about you. ScoreCite identifies these barriers and removes them.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Our Technology */}
        <section className="pb-16 px-4 border-t border-white/5 pt-16 bg-gradient-to-b from-transparent to-blue-950/10">
          <div className="max-w-3xl mx-auto">
            <div className="p-6 sm:p-8 rounded-xl border border-white/10 bg-white/[0.02] animate-fade-in-scroll hover:border-white/20 transition-all duration-300">
              <h2 className="font-heading text-lg sm:text-xl font-bold text-white mb-3" data-testid="technology-section">
                Our Technology: An Engine That Understands Business
              </h2>
              <p className="text-zinc-400 text-sm sm:text-base leading-relaxed mb-8">
                ScoreCite is not just a scanner; it is a sophisticated analysis system that operates in three critical stages:
              </p>
              <div className="space-y-5">
                {TECH_STAGES.map((stage, i) => (
                  <div
                    key={stage.title}
                    className="p-5 rounded-lg border border-white/5 bg-white/[0.02] group animate-fade-in-scroll hover:border-white/15 hover:bg-white/[0.03] transition-all duration-300"
                    style={{ animationDelay: `${0.1 + i * 0.1}s` }}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${stage.color} flex items-center justify-center shadow-lg ${stage.shadow} shrink-0 group-hover:scale-110 transition-transform duration-300`}>
                        <stage.icon className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h3 className="text-white font-semibold text-sm mb-1.5">{stage.title}</h3>
                        <p className="text-zinc-400 text-sm leading-relaxed">{stage.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Security - No-Hostage Policy */}
        <section className="pb-16 px-4 border-t border-white/5 pt-16">
          <div className="max-w-3xl mx-auto">
            <div className="p-6 sm:p-8 rounded-xl border border-white/10 bg-white/[0.02] animate-fade-in-scroll hover:border-white/20 transition-all duration-300">
              <h2 className="font-heading text-lg sm:text-xl font-bold text-white mb-3" data-testid="security-section">
                Security and Your Freedom (Our &ldquo;No-Hostage&rdquo; Policy)
              </h2>
              <p className="text-zinc-400 text-sm sm:text-base leading-relaxed mb-6">
                We believe your website should belong to you. Unlike many modern AI applications that demand &ldquo;backdoor&rdquo; access, ScoreCite is built on two non-negotiable principles:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {SECURITY_PRINCIPLES.map((p, i) => (
                  <div
                    key={p.title}
                    className="p-5 rounded-lg border border-white/5 bg-white/[0.02] animate-fade-in-scroll hover:border-white/15 hover:bg-white/[0.03] transition-all duration-300"
                    style={{ animationDelay: `${0.1 + i * 0.1}s` }}
                  >
                    <p.icon className="w-5 h-5 text-blue-400 mb-3" />
                    <h3 className="text-white font-semibold text-sm mb-2">{p.title}</h3>
                    <p className="text-zinc-400 text-xs leading-relaxed">{p.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Why Constant Monitoring */}
        <section className="pb-16 px-4 border-t border-white/5 pt-16">
          <div className="max-w-3xl mx-auto">
            <div className="p-6 sm:p-8 rounded-xl border border-white/10 bg-white/[0.02] animate-fade-in-scroll hover:border-white/20 transition-all duration-300">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg shadow-amber-500/20 shrink-0">
                  <Activity className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="font-heading text-lg sm:text-xl font-bold text-white mb-4" data-testid="monitoring-section">
                    Why Constant Monitoring Matters
                  </h2>
                  <div className="space-y-4 text-zinc-300 text-sm sm:text-base leading-relaxed">
                    <p>
                      The AI market is the most dynamic in technical history. LLM algorithms are updated almost constantly, changing the way they interpret and trust information.
                    </p>
                    <p className="text-zinc-400">
                      What is clear to a robot today might become a barrier tomorrow.
                    </p>
                    <p>
                      ScoreCite allows for the continuous measurement of these shifts. We monitor trends, model updates, and local market realities so you always know your business remains among the top-tier sources recommended by artificial intelligence.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="pb-20 px-4 border-t border-white/5 pt-16">
          <div className="max-w-3xl mx-auto text-center">
            <div className="p-8 sm:p-10 rounded-xl border border-white/10 bg-white/[0.02] animate-fade-in-scroll">
              <p className="text-white font-medium text-base sm:text-lg mb-8">
                See how the future sees you. Run your ScoreCite audit today.
              </p>
              <Link to="/" data-testid="about-cta-audit">
                <Button className="bg-blue-600 hover:bg-blue-700 text-white font-bold h-12 px-8 rounded-sm text-sm transition-all duration-200">
                  Start Your Audit <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>

            {/* Internal Links */}
            <div className="flex flex-wrap gap-4 justify-center text-sm mt-10">
              <Link to="/" className="text-blue-400 hover:text-blue-300 transition-colors">Home</Link>
              <span className="text-zinc-600">&bull;</span>
              <Link to="/pricing" className="text-blue-400 hover:text-blue-300 transition-colors">Pricing</Link>
              <span className="text-zinc-600">&bull;</span>
              <Link to="/sample-report" className="text-blue-400 hover:text-blue-300 transition-colors">Sample Report</Link>
              <span className="text-zinc-600">&bull;</span>
              <Link to="/faq" className="text-blue-400 hover:text-blue-300 transition-colors">FAQ</Link>
            </div>

            <div className="mt-6">
              <Link to="/" className="text-zinc-500 hover:text-zinc-400 text-sm">&larr; Back to home</Link>
            </div>
          </div>
        </section>

      </div>
    </>
  );
}
